import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Drawer, IconButton, List, Tooltip, useTheme} from '@mui/material';
import {MenuOpen as MenuOpenIcon} from '@mui/icons-material';

const drawerWidth = 256;

const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  position: 'relative',
});

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  position: 'relative',
});

export const SidebarContext = React.createContext();

/**
 * Sidebar for use as local navigation in Planet apps
 */
const Sidebar = ({children, defaultOpen = true, onToggle}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  const toggleDrawerOpen = () => {
    setOpen(!open);
    if (onToggle) {
      onToggle(!open);
    }
  };

  return (
    <SidebarContext.Provider value={{open}}>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
          }),
          ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
          }),
        }}
        PaperProps={{
          elevation: 1,
        }}
        variant="permanent"
        anchor="left"
        open={open}
      >
        <Tooltip
          title={open ? 'Collapse menu' : 'Expand menu'}
          placement="right"
          arrow
        >
          <IconButton
            onClick={toggleDrawerOpen}
            sx={{
              alignSelf: open ? 'flex-end' : 'flex-start',
              borderRadius: 0,
              borderBottomLeftRadius: open ? 8 : 0,
              borderTopLeftRadius: open ? 0 : 8,
              transform: open ? 'none' : 'rotate(180deg)',
              backgroundColor: 'action.focus',
              width: 40,
              height: 30,
            }}
          >
            <MenuOpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <List sx={{padding: 1}}>{children}</List>
      </Drawer>
    </SidebarContext.Provider>
  );
};

Sidebar.propTypes = {
  /**
   Contents of the sidebar
  */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   Whether the sidebar is open by default
  */
  defaultOpen: PropTypes.bool,
  /**
   Callback for when the sidebar is toggled
  */
  onToggle: PropTypes.func,
};

export default Sidebar;
